import React from "react";
import { CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";

const ShieldCheckIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#A655F8] shrink-0">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 11 2 2 4-4" />
  </svg>
);

interface ImportDropzoneProps {
  file: File | null;
  isDragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onReset: () => void;
}

export const ImportDropzone: React.FC<ImportDropzoneProps> = ({
  file,
  isDragging,
  fileInputRef,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onReset,
}) => {
  return (
    <div className="lg:col-span-7 space-y-5">
      {!file ? (
        /* Drag & Drop File Box */
        <div
          className={`border-2 border-dashed rounded-[20px] p-8 text-center transition-all flex flex-col items-center justify-center min-h-[330px] cursor-pointer ${
            isDragging
              ? 'border-[#A655F8] bg-[#A655F8]/5'
              : 'border-[#E2E8F0] hover:border-[#A655F8]/40 bg-white shadow-sm'
          }`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <Input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={onFileChange}
            className="hidden"
          />

          {/* CSV Premium Icon Wrapper */}
          <div className="w-[114px] h-[128px] bg-[#EDE9FE] rounded-[16px] flex items-center justify-center overflow-hidden">
            <div className="relative w-[96px] h-[128px] bg-white rounded-[12px] border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.03)] flex flex-col items-center justify-center">
              {/* Folded Corner */}
              <div className="absolute top-0 -right-1 w-7 h-7 bg-[#EDE9FE] rounded-bl-[8px]" />

              {/* Purple CSV Badge */}
              <div className="bg-[#8B5CF6] text-white text-[18px] font-bold px-3.5 py-1.5 rounded-[6px] tracking-wider shadow-[0_4px_12px_rgba(139,92,246,0.2)]">
                CSV
              </div>

              {/* Decorative Elements */}
              <span className="absolute top-2 left-2 text-[#A655F8]/40 text-[10px]">✨</span>
              <span className="absolute bottom-2 right-2 text-[#A655F8]/40 text-[10px]">✨</span>
            </div>
          </div>

          <h3 className="text-[20px] font-semibold text-slate-800 mt-6">
            Drag & drop your CSV file here
          </h3>
          <p className="text-[16px] text-slate-400 mt-1 font-normal">
            or click to browse
          </p>

          <button
            type="button"
            className="mt-6 h-10 px-5 bg-[#8B5CF6] text-white text-[16px] font-semibold rounded-[10px] flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(166,85,248,0.2)] hover:bg-[#9042E0] transition-colors focus:outline-none"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-0.5">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" x2="12" y1="3" y2="15" />
            </svg>
            Choose CSV File
          </button>
        </div>
      ) : (
        /* Selected File Card */
        <div className="border border-slate-200/80 rounded-[20px] p-8 bg-slate-50/50 flex flex-col items-center justify-center min-h-[330px] shadow-sm">
          <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center text-[#10B981] mb-5">
            <CheckCircle className="w-8 h-8 stroke-[2]" />
          </div>
          <h4 className="text-[16px] font-bold text-slate-800 text-center truncate max-w-full px-6">
            {file.name}
          </h4>
          <p className="text-[13px] text-slate-500 mt-1 font-medium">
            {(file.size / 1024).toFixed(1)} KB
          </p>

          <div className="flex items-center gap-3 mt-8">
            <button
              type="button"
              onClick={onReset}
              className="h-10 px-5 border border-slate-200 rounded-[10px] bg-white text-slate-600 font-semibold text-[13px] hover:bg-slate-50 hover:text-slate-800 transition-colors focus:outline-none shadow-sm"
            >
              Remove File
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="h-10 px-5 bg-[#A655F8] text-white font-semibold text-[13px] rounded-[10px] hover:bg-[#9042E0] transition-colors focus:outline-none shadow-sm"
            >
              Change File
            </button>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={onFileChange}
              className="hidden"
            />
          </div>
        </div>
      )}

      {/* Bottom Security Note Banner */}
      <div className="bg-[#F5F3FF] border border-[#A655F8]/10 p-4 rounded-[14px] flex flex-row items-center gap-3">
        <ShieldCheckIcon />
        <span className="text-[13px] text-slate-600 font-medium leading-relaxed">
          Your file is secure and will only be used to import influencer data.
        </span>
      </div>
    </div>
  );
};
